import { useState, useEffect } from 'react';
import { ref, get, set } from 'firebase/database';
import { HiOutlineExclamationCircle, HiOutlineClock, HiOutlineChartBar } from 'react-icons/hi';
import { db } from '../firebase';
import { useAdminToast } from '../admin/AdminLayout';
import {
  ANALYSIS_JOB_REF,
  SEND_DAILY_REPORT_EMAIL_REF,
  SEND_URGENT_TELEGRAM_NOTIFICATION_REF,
  ANALYZE_NUM_OF_STOCKS_REF,
  ANALYSIS_API_REF,
  ANALYSIS_API_MASSIVE,
  ANALYSIS_API_OPTIONS,
  DATA_FRESHNESS_HOURS_REF,
  REPORT_SIZE_REF,
  SYSTEM_MAINTENANCE_REF,
  DAILY_REPORT_EMAIL_OFF,
  DAILY_REPORT_EMAIL_TIMES,
  formatTimeLabel,
  URGENT_TELEGRAM_OFF,
  URGENT_TELEGRAM_SCORES,
  FRESHNESS_HOURS_OPTIONS,
  REPORT_SIZE_OPTIONS,
  ANALYSIS_REF,
  REPORT_PUBLISH_REF,
  REPORT_WORKSPACE_REF,
  WATCHLIST_REF,
  stripTimestampsFromWatchlist,
} from '../admin/adminConstants';

function AdminConfigurations() {
  const { showToast } = useAdminToast();

  const [schedulerOn, setSchedulerOn] = useState(false);
  const [schedulerLoading, setSchedulerLoading] = useState(true);
  const [schedulerError, setSchedulerError] = useState(null);
  const [schedulerSaving, setSchedulerSaving] = useState(false);

  const [dailyReportEmail, setDailyReportEmail] = useState(DAILY_REPORT_EMAIL_OFF);
  const [dailyReportEmailLoading, setDailyReportEmailLoading] = useState(true);
  const [dailyReportEmailError, setDailyReportEmailError] = useState(null);
  const [dailyReportEmailSaving, setDailyReportEmailSaving] = useState(false);

  const [urgentTelegram, setUrgentTelegram] = useState(URGENT_TELEGRAM_OFF);
  const [urgentTelegramLoading, setUrgentTelegramLoading] = useState(true);
  const [urgentTelegramError, setUrgentTelegramError] = useState(null);
  const [urgentTelegramSaving, setUrgentTelegramSaving] = useState(false);

  const [numOfStocks, setNumOfStocks] = useState('');
  const [numOfStocksLoading, setNumOfStocksLoading] = useState(true);
  const [numOfStocksError, setNumOfStocksError] = useState(null);
  const [numOfStocksSaving, setNumOfStocksSaving] = useState(false);

  const [freshnessHours, setFreshnessHours] = useState('');
  const [freshnessHoursLoading, setFreshnessHoursLoading] = useState(true);
  const [freshnessHoursError, setFreshnessHoursError] = useState(null);
  const [freshnessHoursSaving, setFreshnessHoursSaving] = useState(false);

  const [reportSize, setReportSize] = useState('');
  const [reportSizeLoading, setReportSizeLoading] = useState(true);
  const [reportSizeError, setReportSizeError] = useState(null);
  const [reportSizeSaving, setReportSizeSaving] = useState(false);

  const [analysisApi, setAnalysisApi] = useState(ANALYSIS_API_MASSIVE);
  const [analysisApiLoading, setAnalysisApiLoading] = useState(true);
  const [analysisApiError, setAnalysisApiError] = useState(null);
  const [analysisApiSaving, setAnalysisApiSaving] = useState(false);

  const [resetAnalysisLoading, setResetAnalysisLoading] = useState(false);
  const [resetAnalysisError, setResetAnalysisError] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const [systemMaintenance, setSystemMaintenance] = useState(false);
  const [systemMaintenanceLoading, setSystemMaintenanceLoading] = useState(true);
  const [systemMaintenanceError, setSystemMaintenanceError] = useState(null);
  const [systemMaintenanceSaving, setSystemMaintenanceSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [jobSnap, emailSnap, telegramSnap, numSnap, freshSnap, reportSnap, apiSnap, maintenanceSnap] = await Promise.all([
          get(ref(db, ANALYSIS_JOB_REF)),
          get(ref(db, SEND_DAILY_REPORT_EMAIL_REF)),
          get(ref(db, SEND_URGENT_TELEGRAM_NOTIFICATION_REF)),
          get(ref(db, ANALYZE_NUM_OF_STOCKS_REF)),
          get(ref(db, DATA_FRESHNESS_HOURS_REF)),
          get(ref(db, REPORT_SIZE_REF)),
          get(ref(db, ANALYSIS_API_REF)),
          get(ref(db, SYSTEM_MAINTENANCE_REF)),
        ]);
        if (cancelled) return;
        setSchedulerOn(jobSnap.val() === true);
        const ev = emailSnap.val();
        if (ev === true) setDailyReportEmail('09:00');
        else if (ev === false || ev == null) setDailyReportEmail(DAILY_REPORT_EMAIL_OFF);
        else if (typeof ev === 'string') {
          const n = ev.toLowerCase().trim() === 'off' ? DAILY_REPORT_EMAIL_OFF : ev;
          setDailyReportEmail(DAILY_REPORT_EMAIL_TIMES.includes(n) ? n : DAILY_REPORT_EMAIL_OFF);
        } else setDailyReportEmail(DAILY_REPORT_EMAIL_OFF);
        const tv = telegramSnap.val();
        if (tv === true) setUrgentTelegram('10');
        else if (tv === false || tv == null) setUrgentTelegram(URGENT_TELEGRAM_OFF);
        else if (typeof tv === 'number') setUrgentTelegram(URGENT_TELEGRAM_SCORES.includes(String(tv)) ? String(tv) : URGENT_TELEGRAM_OFF);
        else if (typeof tv === 'string') setUrgentTelegram(tv.toLowerCase().trim() === 'off' ? URGENT_TELEGRAM_OFF : (URGENT_TELEGRAM_SCORES.includes(tv) ? tv : URGENT_TELEGRAM_OFF));
        else setUrgentTelegram(URGENT_TELEGRAM_OFF);
        const nv = numSnap.val();
        const n = typeof nv === 'number' && Number.isInteger(nv) ? nv : 1;
        setNumOfStocks(String(Math.min(10, Math.max(1, n))));
        const fv = freshSnap.val();
        const fn = typeof fv === 'number' && Number.isInteger(fv) && fv >= 0 ? fv : 1;
        const clampedF = FRESHNESS_HOURS_OPTIONS.includes(fn) ? fn : (FRESHNESS_HOURS_OPTIONS.find((h) => h >= fn) ?? FRESHNESS_HOURS_OPTIONS[FRESHNESS_HOURS_OPTIONS.length - 1]);
        setFreshnessHours(String(clampedF));
        const rpv = reportSnap.val();
        const rpn = typeof rpv === 'number' && Number.isInteger(rpv) && rpv >= 0 ? rpv : 1;
        setReportSize(String(Math.min(25, Math.max(1, rpn))));
        const apiVal = apiSnap.val();
        const apiStr = typeof apiVal === 'string' ? apiVal.trim().toLowerCase() : '';
        setAnalysisApi(ANALYSIS_API_OPTIONS.some((o) => o.value === apiStr) ? apiStr : ANALYSIS_API_MASSIVE);
        setSystemMaintenance(maintenanceSnap.val() === true);
      } catch (err) {
        if (!cancelled) setSchedulerError(err.message || 'Failed to load config');
      } finally {
        if (!cancelled) {
          setSchedulerLoading(false);
          setDailyReportEmailLoading(false);
          setUrgentTelegramLoading(false);
          setNumOfStocksLoading(false);
          setFreshnessHoursLoading(false);
          setReportSizeLoading(false);
          setAnalysisApiLoading(false);
          setSystemMaintenanceLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSchedulerToggle = async () => {
    const next = !schedulerOn;
    setSchedulerError(null);
    setSchedulerSaving(true);
    try {
      await set(ref(db, ANALYSIS_JOB_REF), next);
      setSchedulerOn(next);
      showToast('Analysis job updated');
    } catch (err) {
      setSchedulerError(err.message || 'Failed to update analysis job');
    } finally {
      setSchedulerSaving(false);
    }
  };

  const handleDailyReportEmailChange = async (e) => {
    const next = e.target.value;
    setDailyReportEmailError(null);
    setDailyReportEmailSaving(true);
    try {
      await set(ref(db, SEND_DAILY_REPORT_EMAIL_REF), next);
      setDailyReportEmail(next);
      showToast('Daily report email updated');
    } catch (err) {
      setDailyReportEmailError(err.message || 'Failed to update daily report email');
    } finally {
      setDailyReportEmailSaving(false);
    }
  };

  const handleUrgentTelegramChange = async (e) => {
    const next = e.target.value;
    setUrgentTelegramError(null);
    setUrgentTelegramSaving(true);
    try {
      const saveValue = next === URGENT_TELEGRAM_OFF ? next : parseFloat(next, 10);
      await set(ref(db, SEND_URGENT_TELEGRAM_NOTIFICATION_REF), saveValue);
      setUrgentTelegram(next);
      showToast('Telegram notification on updated');
    } catch (err) {
      setUrgentTelegramError(err.message || 'Failed to update Telegram notification on');
    } finally {
      setUrgentTelegramSaving(false);
    }
  };

  const handleNumOfStocksChange = async (e) => {
    const next = e.target.value;
    const parsed = parseInt(next, 10);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > 10) return;
    setNumOfStocksError(null);
    setNumOfStocks(next);
    setNumOfStocksSaving(true);
    try {
      await set(ref(db, ANALYZE_NUM_OF_STOCKS_REF), parsed);
      showToast('Stocks per run updated');
    } catch (err) {
      setNumOfStocksError(err.message || 'Failed to update stocks per run');
    } finally {
      setNumOfStocksSaving(false);
    }
  };

  const handleFreshnessHoursChange = async (e) => {
    const next = e.target.value;
    const parsed = parseInt(next, 10);
    if (!FRESHNESS_HOURS_OPTIONS.includes(parsed)) return;
    setFreshnessHoursError(null);
    setFreshnessHours(next);
    setFreshnessHoursSaving(true);
    try {
      await set(ref(db, DATA_FRESHNESS_HOURS_REF), parsed);
      showToast('Data freshness updated');
    } catch (err) {
      setFreshnessHoursError(err.message || 'Failed to update data freshness');
    } finally {
      setFreshnessHoursSaving(false);
    }
  };

  const handleReportSizeChange = async (e) => {
    const next = e.target.value;
    const parsed = parseInt(next, 10);
    if (parsed < 1 || parsed > 25) return;
    setReportSizeError(null);
    setReportSize(next);
    setReportSizeSaving(true);
    try {
      await set(ref(db, REPORT_SIZE_REF), parsed);
      showToast('Daily report recommendation list size updated');
    } catch (err) {
      setReportSizeError(err.message || 'Failed to update Daily report recommendation list size');
    } finally {
      setReportSizeSaving(false);
    }
  };

  const handleAnalysisApiChange = async (e) => {
    const next = e.target.value;
    if (!ANALYSIS_API_OPTIONS.some((o) => o.value === next)) return;
    setAnalysisApiError(null);
    setAnalysisApi(next);
    setAnalysisApiSaving(true);
    try {
      await set(ref(db, ANALYSIS_API_REF), next);
      showToast('Analysis API updated');
    } catch (err) {
      setAnalysisApiError(err.message || 'Failed to update analysis API');
    } finally {
      setAnalysisApiSaving(false);
    }
  };

  const handleSystemMaintenanceToggle = async () => {
    const next = !systemMaintenance;
    setSystemMaintenanceError(null);
    setSystemMaintenanceSaving(true);
    try {
      await set(ref(db, SYSTEM_MAINTENANCE_REF), next);
      setSystemMaintenance(next);
      showToast(next ? 'System maintenance enabled' : 'System maintenance disabled');
    } catch (err) {
      setSystemMaintenanceError(err.message || 'Failed to update system maintenance');
    } finally {
      setSystemMaintenanceSaving(false);
    }
  };

  const handleResetAnalysis = async () => {
    setShowResetConfirm(false);
    setResetAnalysisError(null);
    setResetAnalysisLoading(true);
    try {
      await Promise.all([
        set(ref(db, ANALYSIS_REF), null),
        set(ref(db, REPORT_PUBLISH_REF), null),
        set(ref(db, REPORT_WORKSPACE_REF), null),
      ]);
      const watchlistSnap = await get(ref(db, WATCHLIST_REF));
      const watchlistData = watchlistSnap.val();
      if (watchlistData && typeof watchlistData === 'object') {
        const cleaned = stripTimestampsFromWatchlist(watchlistData);
        await set(ref(db, WATCHLIST_REF), cleaned);
      }
      showToast('Analysis data reset');
    } catch (err) {
      setResetAnalysisError(err.message || 'Failed to reset analysis');
    } finally {
      setResetAnalysisLoading(false);
    }
  };

  return (
    <div className="admin-cards">
      <section className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <HiOutlineExclamationCircle className="admin-card-icon" aria-hidden />
            System
          </h2>
        </div>
        <div className="admin-card-body">
          <ul className="settings-group-list">
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">System maintenance</span>
                <span className="settings-row-desc">When on, all pages except Admin show a system maintenance message.</span>
              </div>
              <div className="settings-row-control">
                {systemMaintenanceLoading ? (
                  <span className="settings-switch-loading">Loading…</span>
                ) : (
                  <label className="settings-switch">
                    <input type="checkbox" checked={systemMaintenance} onChange={handleSystemMaintenanceToggle} disabled={systemMaintenanceSaving} className="settings-switch-input" />
                    <span className="settings-switch-slider" />
                    <span className="settings-switch-label">{systemMaintenance ? 'On' : 'Off'}</span>
                  </label>
                )}
              </div>
            </li>
            {systemMaintenanceError && <li className="settings-row settings-row--error" role="alert">{systemMaintenanceError}</li>}
          </ul>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <HiOutlineClock className="admin-card-icon" aria-hidden />
            Automation & notifications
          </h2>
        </div>
        <div className="admin-card-body">
          <ul className="settings-group-list">
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Analysis job</span>
                <span className="settings-row-desc">Turn the analysis job on or off.</span>
              </div>
              <div className="settings-row-control">
                {schedulerLoading ? (
                  <span className="settings-switch-loading">Loading…</span>
                ) : (
                  <label className="settings-switch">
                    <input type="checkbox" checked={schedulerOn} onChange={handleSchedulerToggle} disabled={schedulerSaving} className="settings-switch-input" />
                    <span className="settings-switch-slider" />
                    <span className="settings-switch-label">{schedulerOn ? 'On' : 'Off'}</span>
                  </label>
                )}
              </div>
            </li>
            {schedulerError && <li className="settings-row settings-row--error" role="alert">{schedulerError}</li>}
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Daily report email at</span>
                <span className="settings-row-desc">When to send the daily report by email.</span>
              </div>
              <div className="settings-row-control">
                {dailyReportEmailLoading ? <span className="settings-switch-loading">Loading…</span> : (
                  <select value={dailyReportEmail} onChange={handleDailyReportEmailChange} disabled={dailyReportEmailSaving} className="settings-select settings-select--inline">
                    {DAILY_REPORT_EMAIL_TIMES.map((opt) => <option key={opt} value={opt}>{formatTimeLabel(opt)}</option>)}
                  </select>
                )}
              </div>
            </li>
            {dailyReportEmailError && <li className="settings-row settings-row--error" role="alert">{dailyReportEmailError}</li>}
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Telegram notification on</span>
                <span className="settings-row-desc">Alert when score is at or above threshold.</span>
              </div>
              <div className="settings-row-control">
                {urgentTelegramLoading ? <span className="settings-switch-loading">Loading…</span> : (
                  <select value={urgentTelegram} onChange={handleUrgentTelegramChange} disabled={urgentTelegramSaving} className="settings-select settings-select--inline">
                    {URGENT_TELEGRAM_SCORES.map((opt) => <option key={opt} value={opt}>{opt === URGENT_TELEGRAM_OFF ? 'Off' : opt}</option>)}
                  </select>
                )}
              </div>
            </li>
            {urgentTelegramError && <li className="settings-row settings-row--error" role="alert">{urgentTelegramError}</li>}
          </ul>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <HiOutlineChartBar className="admin-card-icon" aria-hidden />
            Analysis & data limits
          </h2>
        </div>
        <div className="admin-card-body">
          <ul className="settings-group-list">
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Stocks per run</span>
                <span className="settings-row-desc">How many stocks the scheduler analyzes on every run.</span>
              </div>
              <div className="settings-row-control">
                {numOfStocksLoading ? <span className="settings-switch-loading">Loading…</span> : (
                  <select value={numOfStocks} onChange={handleNumOfStocksChange} disabled={numOfStocksSaving} className="settings-select settings-select--inline">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                )}
              </div>
            </li>
            {numOfStocksError && <li className="settings-row settings-row--error" role="alert">{numOfStocksError}</li>}
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Data freshness</span>
                <span className="settings-row-desc">Max age of data in hours (backend).</span>
              </div>
              <div className="settings-row-control">
                {freshnessHoursLoading ? <span className="settings-switch-loading">Loading…</span> : (
                  <select value={freshnessHours} onChange={handleFreshnessHoursChange} disabled={freshnessHoursSaving} className="settings-select settings-select--inline">
                    {FRESHNESS_HOURS_OPTIONS.map((h) => <option key={h} value={h}>{h} hr</option>)}
                  </select>
                )}
              </div>
            </li>
            {freshnessHoursError && <li className="settings-row settings-row--error" role="alert">{freshnessHoursError}</li>}
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Daily report recommendation list size</span>
                <span className="settings-row-desc">Number of stocks shown in each buy and sell section on daily reports.</span>
              </div>
              <div className="settings-row-control">
                {reportSizeLoading ? <span className="settings-switch-loading">Loading…</span> : (
                  <select value={reportSize} onChange={handleReportSizeChange} disabled={reportSizeSaving} className="settings-select settings-select--inline">
                    {REPORT_SIZE_OPTIONS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                )}
              </div>
            </li>
            {reportSizeError && <li className="settings-row settings-row--error" role="alert">{reportSizeError}</li>}
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">API</span>
                <span className="settings-row-desc">Market data provider used when the scheduler and analyze endpoint run.</span>
              </div>
              <div className="settings-row-control">
                {analysisApiLoading ? <span className="settings-switch-loading">Loading…</span> : (
                  <select value={analysisApi} onChange={handleAnalysisApiChange} disabled={analysisApiSaving} className="settings-select settings-select--inline">
                    {ANALYSIS_API_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                )}
              </div>
            </li>
            {analysisApiError && <li className="settings-row settings-row--error" role="alert">{analysisApiError}</li>}
            <li className="settings-row">
              <div className="settings-row-label">
                <span className="settings-row-title">Reset analysis</span>
                <span className="settings-row-desc">Delete all analysis, reports, and workspace. Cannot be undone.</span>
              </div>
              <div className="settings-row-control">
                <button type="button" className="btn btn-danger" onClick={() => setShowResetConfirm(true)} disabled={resetAnalysisLoading}>
                  {resetAnalysisLoading ? 'Resetting…' : 'Reset analysis'}
                </button>
              </div>
            </li>
            {resetAnalysisError && <li className="settings-row settings-row--error" role="alert">{resetAnalysisError}</li>}
          </ul>
          {showResetConfirm && (
            <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="reset-confirm-title" aria-describedby="reset-confirm-desc">
              <div className="modal-dialog">
                <h2 id="reset-confirm-title" className="modal-title">Reset analysis?</h2>
                <p id="reset-confirm-desc" className="modal-desc">
                  This will permanently delete all analysis data, published reports, and report workspace. This cannot be undone.
                </p>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowResetConfirm(false)}>Cancel</button>
                  <button type="button" className="btn btn-danger" onClick={handleResetAnalysis} disabled={resetAnalysisLoading}>
                    {resetAnalysisLoading ? 'Resetting…' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default AdminConfigurations;
